// noinspection GraphQLUnresolvedReference

import { useMutation } from '@tanstack/react-query'

import type { GraphqlClientOptionsProps } from '../../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../../base/api/graphqlClient.ts'
import type { MutationOpts } from '../../../../base/types/reactQuery.ts'
import { gqlAst } from '../../../../utils/gql.ts'

const query = /* GraphQL */ gqlAst`
  mutation IMPRESORA_ASIGNACION_ELIMINAR($id: ID!) {
    impresoraAsignacionEliminar(id: $id)
  }
`

export const MUTATION_KEY_IMPRESORA_ASIGNACION_ELIMINAR = ['impresora-asignacion-eliminar'] as const

export interface ApiImpresoraAsignacionEliminarParams {
  /** Id de la asignación a eliminar */
  id: string
}

/**
 * Servicio para eliminar una asignación de impresora.
 * La asignación es configuración sin historial: el backend la borra físicamente.
 * @param params Id de la asignación.
 * @param options Headers adicionales y señal de cancelación de la petición.
 * @returns `true` si el backend confirmó la eliminación.
 * @author isi-template
 */
export const apiImpresoraAsignacionEliminar = async (
  params: ApiImpresoraAsignacionEliminarParams,
  options: GraphqlClientOptionsProps = {},
): Promise<boolean> => {
  try {
    const data = await graphqlClient.request<{ impresoraAsignacionEliminar: boolean }>({
      document: query,
      variables: { ...params },
      requestHeaders: options.headers,
      signal: options.signal,
    })
    return data.impresoraAsignacionEliminar
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Hook de mutación para eliminar una asignación de impresora.
 * Para refrescar al terminar:
 * `onSuccess: () => Promise.all([invalidateImpresoraAsignacionListado(), invalidateImpresoraPuntoVenta()])`.
 * @param options Opciones de React Query (onSuccess, onError, ...).
 * @author isi-template
 */
export const useImpresoraAsignacionEliminar = (
  options?: MutationOpts<boolean, ApiImpresoraAsignacionEliminarParams>,
) => {
  return useMutation({
    mutationKey: MUTATION_KEY_IMPRESORA_ASIGNACION_ELIMINAR,
    ...options,
    mutationFn: (params: ApiImpresoraAsignacionEliminarParams) => apiImpresoraAsignacionEliminar(params),
  })
}
